// Run E2 J2 round 2: teacher dashboard chrome (headings, tabs, buttons,
// table headers) in English and Spanish. Used with useT() from stores/uiLang.

export const TD_T = {
  en: {
    // TeacherInformation
    myInfo: "My Information",
    school: "School",
    noSchool: "No school assigned",
    district: "District",
    noDistrict: "No district assigned",
    myClasses: "My Classes",
    noClasses: "No classes assigned",
    // Overview
    quickStats: "Quick Stats",
    totalStudents: "Total Students Enrolled",
    pendingGrading: "Assignments Pending Grading",
    avgPerformance: "Average Performance",
    // Grade distribution
    gradeDistribution: "Grade Distribution",
    selectClass: "Select Class",
    selectedClass: "Selected Class",
    numStudents: "Number of Students",
    gradeRanges: "Grade Ranges",
    loadingDistribution: "Loading grade distribution...",
    // Class Management
    classManagement: "Class Management",
    addClass: "Add New Class",
    syncGoogle: "Sync with Google Classroom",
    importStudents: "Import Students",
    deleteStudent: "Delete Student",
    searchClasses: "Search classes",
    searchClassesPh: "Search classes by name...",
    importResults: "Import Results",
    importSummary: "Summary of the student import operation",
    syncResults: "Google Classroom Sync Results",
    syncSummary: "Summary of the Google Classroom sync operation",
    rosterResults: "Class Roster Results",
    rosterSummary: "Summary of the class roster operation",
    downloadNew: "Download New Students Info",
    joinCodes: "Class Join Codes",
    joinCodesHelp: "Share these codes with students to let them join your classes",
    className: "Class Name",
    joinCode: "Join Code",
    expiration: "Expiration",
    actions: "Actions",
    // ClassCard / ClassCodeRow
    classTime: "Class Time",
    notSpecified: "Not specified",
    students: "Students",
    gradeType: "Grade Type",
    copyJoinCode: "Copy join code",
    noJoinCode: "No join code",
    lastSynced: "Last Synced",
    activeClass: "Active",
    rostering: "Rostering...",
    roster: "Roster",
    manage: "Manage",
    delete: "Delete",
    copy: "Copy",
    reset: "Reset",
    never: "Never",
    // Student Progress
    studentProgress: "Student Progress",
    searchStudents: "Search students...",
    allClasses: "All Classes",
    previous: "Previous",
    next: "Next",
    showing: (a: number, b: number, n: number) => `Showing ${a} to ${b} of ${n} students`,
    loadingStudents: "Loading students...",
    loadingClasses: "Loading classes...",
    noClassesFound: "No Classes Found",
    noClassesHelp: "You don't have any classes yet. Create a class to start tracking student progress.",
    studentName: "Student Name",
    classCol: "Class",
    performance: "Performance",
    assignment: "Assignment",
    lastSubmitted: "Last Submitted Date",
    lastLogin: "Last Login",
    status: "Status",
    noStudentsFound: "No Students Found",
    noStudentsHelp: "There are no students in your classes yet. Add students to start tracking their progress.",
    perfLabels: {
      Active: "Active",
      "Below Average": "Below Average",
      "Needs Attention": "Needs Attention",
      "No assignments": "No assignments",
      "No Grade": "No Grade",
    } as Record<string, string>,
    // Assignments
    assignmentsTitle: "Assignments Management",
    searchAssignments: "Search assignments...",
    filterLabels: { All: "All", Quizzes: "Quizzes" } as Record<string, string>,
    activeAssignments: "Active Assignments",
    sortBy: "Sort by:",
    dueDate: "Due Date",
    startDate: "Start Date",
    createdDate: "Created Date",
    loadingAssignments: "Loading assignments...",
    individual: "Individual",
    view: "View",
    edit: "Edit",
    tableHeaders: ["Assignment Name", "Type", "Standard/Chapter", "Assigned To", "Start Date", "Due Date", "Retakes", "Completion", ""],
    chapterSort: "Chapter",
    typeChapter: "Chapter",
    allStudents: "All Students",
    selectStudents: (n: number) => `Select Students (${n})`,
    daysLeft: (n: number) => `${n} days left`,
    dueToday: "Due today or past due",
    completed: (p: number, a: number, b: number) => `${p}% Completed (${a}/${b})`,
    addScale: "Add Scale",
    submissionsTitle: "Assignment Submissions",
    submissionsSub: "Review and adjust student submissions and AI-generated grades",
    optCompleted: "Completed",
    optOverdue: "Overdue",
    filterByStudent: "Filter by student...",
    loadingSubmissions: "Loading submissions...",
    noSubmissions: "No submissions found",
    typeCol: "Type",
    submitted: "Submitted",
    gradeCol: "Grade",
    rangeAssignments: (a: number, b: number, n: number) => `${a}-${b} of ${n} assignments`,
    noAssignments: "No assignments found",
    // Gradebook
    gradebook: "Gradebook",
    gradebookSub: "View student progress by chapter. Click on a cell to review submissions.",
    search: "Search:",
    searchByName: "Search by student name...",
    showPercent: "Show grades as percentage",
    classLabel: "Class:",
    allRecords: "All records",
    loadingGradebook: "Loading gradebook...",
    noAssignmentsGb: "No assignments found. Create assignments to see them in the gradebook.",
    noAssignmentsFilter: "No assignments found for this filter. Try another one.",
    student: "Student",
    legendComplete: "Complete (≥70%)",
    legendInProgress: "In Progress",
    legendNeedsReview: "Needs Review (<70%)",
    legendNotStarted: "Not Started",
    // Grade Scales
    gradeScales: "Grade Scales",
    noCustomScales: "No custom scales yet.",
    deleteScaleTitle: "Delete Grade Scale",
    deleteScaleConfirm: "Are you sure you want to delete this grade scale? This action cannot be undone.",
    cancel: "Cancel",
    save: "Save",
    scaleName: "Scale Name",
    scaleNamePh: "E.g., Participation Rubric or Mastery 4-Point",
    scaleValues: "Scale Values",
    label: "Label",
    remove: "Remove",
  },
  es: {
    myInfo: "Mi información",
    school: "Escuela",
    noSchool: "Sin escuela asignada",
    district: "Distrito",
    noDistrict: "Sin distrito asignado",
    myClasses: "Mis clases",
    noClasses: "Sin clases asignadas",
    quickStats: "Datos rápidos",
    totalStudents: "Estudiantes inscritos",
    pendingGrading: "Tareas por calificar",
    avgPerformance: "Rendimiento promedio",
    gradeDistribution: "Distribución de calificaciones",
    selectClass: "Elige una clase",
    selectedClass: "Clase elegida",
    numStudents: "Número de estudiantes",
    gradeRanges: "Rangos de calificación",
    loadingDistribution: "Cargando la distribución de calificaciones...",
    classManagement: "Administrar clases",
    addClass: "Agregar una clase",
    syncGoogle: "Sincronizar con Google Classroom",
    importStudents: "Importar estudiantes",
    deleteStudent: "Eliminar estudiante",
    searchClasses: "Buscar clases",
    searchClassesPh: "Busca clases por nombre...",
    importResults: "Resultados de la importación",
    importSummary: "Resumen de la importación de estudiantes",
    syncResults: "Resultados de la sincronización con Google Classroom",
    syncSummary: "Resumen de la sincronización con Google Classroom",
    rosterResults: "Resultados de la lista de la clase",
    rosterSummary: "Resumen de la carga de la lista de la clase",
    downloadNew: "Descargar los datos de los estudiantes nuevos",
    joinCodes: "Códigos para unirse a las clases",
    joinCodesHelp: "Comparte estos códigos con tus estudiantes para que se unan a tus clases",
    className: "Nombre de la clase",
    joinCode: "Código para unirse",
    expiration: "Vencimiento",
    actions: "Acciones",
    classTime: "Horario de la clase",
    notSpecified: "Sin especificar",
    students: "Estudiantes",
    gradeType: "Tipo de calificación",
    copyJoinCode: "Copiar el código",
    noJoinCode: "Sin código",
    lastSynced: "Última sincronización",
    activeClass: "Activa",
    rostering: "Cargando la lista...",
    roster: "Cargar la lista",
    manage: "Administrar",
    delete: "Eliminar",
    copy: "Copiar",
    reset: "Restablecer",
    never: "Nunca",
    studentProgress: "Progreso de los estudiantes",
    searchStudents: "Buscar estudiantes...",
    allClasses: "Todas las clases",
    previous: "Anterior",
    next: "Siguiente",
    showing: (a: number, b: number, n: number) => `Mostrando ${a} a ${b} de ${n} estudiantes`,
    loadingStudents: "Cargando estudiantes...",
    loadingClasses: "Cargando clases...",
    noClassesFound: "No hay clases",
    noClassesHelp: "Todavía no tienes clases. Crea una clase para empezar a seguir el progreso de tus estudiantes.",
    studentName: "Nombre del estudiante",
    classCol: "Clase",
    performance: "Rendimiento",
    assignment: "Tarea",
    lastSubmitted: "Última entrega",
    lastLogin: "Último inicio de sesión",
    status: "Estado",
    noStudentsFound: "No hay estudiantes",
    noStudentsHelp: "Todavía no hay estudiantes en tus clases. Agrega estudiantes para empezar a seguir su progreso.",
    perfLabels: {
      Active: "Activo",
      "Below Average": "Bajo el promedio",
      "Needs Attention": "Necesita atención",
      "No assignments": "Sin tareas",
      "No Grade": "Sin calificación",
    } as Record<string, string>,
    assignmentsTitle: "Tareas asignadas",
    searchAssignments: "Buscar tareas...",
    filterLabels: { All: "Todas", Quizzes: "Cuestionarios" } as Record<string, string>,
    activeAssignments: "Tareas activas",
    sortBy: "Ordenar por:",
    dueDate: "Fecha de entrega",
    startDate: "Fecha de inicio",
    createdDate: "Fecha de creación",
    loadingAssignments: "Cargando tareas...",
    individual: "Individual",
    view: "Ver",
    edit: "Editar",
    tableHeaders: ["Nombre de la tarea", "Tipo", "Estándar o capítulo", "Asignada a", "Fecha de inicio", "Fecha de entrega", "Reintentos", "Avance", ""],
    chapterSort: "Capítulo",
    typeChapter: "Capítulo",
    allStudents: "Todos los estudiantes",
    selectStudents: (n: number) => `Algunos estudiantes (${n})`,
    daysLeft: (n: number) => (n === 1 ? "Queda 1 día" : `Quedan ${n} días`),
    dueToday: "Se entrega hoy o ya pasó la fecha",
    completed: (p: number, a: number, b: number) => `${p} % completado (${a}/${b})`,
    addScale: "Agregar una escala",
    submissionsTitle: "Entregas de los estudiantes",
    submissionsSub: "Revisa y ajusta las entregas de los estudiantes y las calificaciones generadas por IA",
    optCompleted: "Terminadas",
    optOverdue: "Atrasadas",
    filterByStudent: "Filtrar por estudiante...",
    loadingSubmissions: "Cargando entregas...",
    noSubmissions: "No hay entregas",
    typeCol: "Tipo",
    submitted: "Entregada",
    gradeCol: "Calificación",
    rangeAssignments: (a: number, b: number, n: number) => `${a}-${b} de ${n} tareas`,
    noAssignments: "No hay tareas",
    gradebook: "Calificaciones",
    gradebookSub: "Mira el progreso de los estudiantes por capítulo. Haz clic en una celda para revisar las entregas.",
    search: "Buscar:",
    searchByName: "Busca por nombre del estudiante...",
    showPercent: "Mostrar las calificaciones en porcentaje",
    classLabel: "Clase:",
    allRecords: "Todos",
    loadingGradebook: "Cargando las calificaciones...",
    noAssignmentsGb: "No hay tareas. Asigna capítulos para verlos aquí.",
    noAssignmentsFilter: "No hay tareas con este filtro. Prueba con otro.",
    student: "Estudiante",
    legendComplete: "Completo (≥70 %)",
    legendInProgress: "En curso",
    legendNeedsReview: "Por revisar (<70 %)",
    legendNotStarted: "Sin empezar",
    gradeScales: "Escalas de calificación",
    noCustomScales: "Todavía no hay escalas personalizadas.",
    deleteScaleTitle: "Eliminar la escala de calificación",
    deleteScaleConfirm: "¿Seguro que quieres eliminar esta escala de calificación? No se puede deshacer.",
    cancel: "Cancelar",
    save: "Guardar",
    scaleName: "Nombre de la escala",
    scaleNamePh: "P. ej., Rúbrica de participación o Dominio de 4 puntos",
    scaleValues: "Valores de la escala",
    label: "Etiqueta",
    remove: "Quitar",
  },
};

// Run E2 J6 (D10): "Review answers" (teacher review queue: dashboard tab and the
// LTI /lti/hitl-queue page). Same EN/ES table pattern as TD_T.
const REVIEW_EN = {
  navLabel: "Review answers",
  eyebrowAll: "Teacher review · all chapters",
  eyebrowChapter: (id: string) => `Teacher review · chapter ${id}`,
  title: "Review answers",
  waiting: (n: number) => (n === 1 ? "1 answer waiting" : `${n} answers waiting`),
  mix: (spoken: number, written: number) => `${spoken} spoken, ${written} written`,
  releasedThisSession: (n: number) => `${n} released this session`,
  refresh: "Refresh",
  loading: "Loading answers...",
  loadFailed: "The answers could not load. Try Refresh.",
  aiAutoRelease: (on: boolean) => `AI auto-release: ${on ? "On" : "Off"}`,
  emptyTitle: "Nothing is waiting for review.",
  emptyBody: "When students turn in a spoken or written answer, it shows up here for you to score and release.",
  spoken: "Spoken answer",
  written: "Written answer",
  tier: (n: string) => `Tier ${n}`,
  question: "Question",
  answer: "Answer",
  listenHelp: "Press play to hear the student.",
  noAudio: "No recording was saved for this answer.",
  noText: "This answer was turned in before written answers were saved, so the text is not available.",
  submittedOn: (d: string) => `Turned in ${d}`,
  maxPoints: (n: number) => `${n} points`,
  scoreButton: "Score",
  dialogTitle: "Score this answer",
  rubric: "Rubric",
  rubricHelp: (max: number) => `Pick a score from 0 to ${max}.`,
  noRubric: "This item has no rubric yet. Pick a score.",
  points: (n: number) => (n === 1 ? "1 point" : `${n} points`),
  aiSuggestion: (n: number, max: number) => `AI suggestion: ${n} of ${max}. Check it before you release.`,
  aiNote: "AI note",
  feedback: "Feedback for the student (optional)",
  cancel: "Cancel",
  release: "Save score and release",
  releasing: "Releasing...",
  pickScore: "Pick a score first.",
  outOfRange: (max: number) => `The score must be between 0 and ${max}.`,
  releaseFailed: "The score could not be released. Try again.",
  released: (name: string, score: number, max: number) => `Released: ${name}, ${score} of ${max}.`,
  close: "Close",
  teachersOnlyTitle: "Review answers",
  teachersOnly: "This screen is for teachers reviewing student answers.",
};

export const REVIEW_T: { en: typeof REVIEW_EN; es: typeof REVIEW_EN } = {
  en: REVIEW_EN,
  es: {
    navLabel: "Revisar respuestas",
    eyebrowAll: "Revisión del maestro · todos los capítulos",
    eyebrowChapter: (id: string) => `Revisión del maestro · capítulo ${id}`,
    title: "Revisar respuestas",
    waiting: (n: number) => (n === 1 ? "1 respuesta en espera" : `${n} respuestas en espera`),
    mix: (spoken: number, written: number) =>
      `${spoken} ${spoken === 1 ? "oral" : "orales"}, ${written} ${written === 1 ? "escrita" : "escritas"}`,
    releasedThisSession: (n: number) => (n === 1 ? "1 publicada en esta sesión" : `${n} publicadas en esta sesión`),
    refresh: "Actualizar",
    loading: "Cargando respuestas...",
    loadFailed: "No se pudieron cargar las respuestas. Prueba con Actualizar.",
    aiAutoRelease: (on: boolean) => `Publicación automática con IA: ${on ? "activada" : "desactivada"}`,
    emptyTitle: "No hay respuestas por revisar.",
    emptyBody: "Cuando los estudiantes entreguen una respuesta oral o escrita, aparecerá aquí para que la califiques y la publiques.",
    spoken: "Respuesta oral",
    written: "Respuesta escrita",
    tier: (n: string) => `Nivel ${n}`,
    question: "Pregunta",
    answer: "Respuesta",
    listenHelp: "Presiona reproducir para escuchar al estudiante.",
    noAudio: "No se guardó ninguna grabación para esta respuesta.",
    noText: "Esta respuesta se entregó antes de que se guardaran las respuestas escritas, así que el texto no está disponible.",
    submittedOn: (d: string) => `Entregada el ${d}`,
    maxPoints: (n: number) => `${n} puntos`,
    scoreButton: "Calificar",
    dialogTitle: "Calificar esta respuesta",
    rubric: "Rúbrica",
    rubricHelp: (max: number) => `Elige una calificación de 0 a ${max}.`,
    noRubric: "Esta pregunta todavía no tiene rúbrica. Elige una calificación.",
    points: (n: number) => (n === 1 ? "1 punto" : `${n} puntos`),
    aiSuggestion: (n: number, max: number) => `Sugerencia de la IA: ${n} de ${max}. Revísala antes de publicar.`,
    aiNote: "Nota de la IA",
    feedback: "Comentario para el estudiante (opcional)",
    cancel: "Cancelar",
    release: "Guardar y publicar la calificación",
    releasing: "Publicando...",
    pickScore: "Primero elige una calificación.",
    outOfRange: (max: number) => `La calificación debe estar entre 0 y ${max}.`,
    releaseFailed: "No se pudo publicar la calificación. Inténtalo de nuevo.",
    released: (name: string, score: number, max: number) => `Publicada: ${name}, ${score} de ${max}.`,
    close: "Cerrar",
    teachersOnlyTitle: "Revisar respuestas",
    teachersOnly: "Esta pantalla es para que los maestros revisen las respuestas de los estudiantes.",
  },
};
